import React from 'react';
import AgentSwarmProgress from '../AgentSwarmProgress';

/**
 * 3-step intake wizard: Product Definition → Market Context → Analysis Setup.
 * The full-screen "analysis loading" modal lives here too so it sits next to
 * the trigger button it belongs to.
 *
 * Pure presentation component — all state + handlers come in via props.
 * Extracted from App.js (Feb 2026) as part of the P2 monolith decomposition.
 * Style: legacy classes preserved 1:1 to avoid visual regression. The next
 * pass applies BCM design-system tokens (see /app/memory/BCM_Design_System.md).
 */
export default function Wizard({
  currentStep,
  formData,
  handleInputChange,
  loadExample,
  uploadedFiles,
  uploadedDocData,
  isUploading,
  handleFileUpload,
  removeUploadedFiles,
  isAnalyzing,
  setIsAnalyzing,
  setAnalysisProgress,
  nextStep,
  prevStep,
  analyzeMarket,
}) {
  return (
          <div className="bg-white rounded-lg shadow-md p-8">
            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                {[1, 2, 3].map((step) => (
                  <div key={step} className="flex items-center">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-medium ${
                      currentStep >= step ? 'bg-orange-500 text-white' : 'bg-gray-300 text-gray-600'
                    }`}>
                      {step}
                    </div>
                    {step < 3 && (
                      <div className={`w-24 h-1 mx-4 rounded ${
                        currentStep > step ? 'bg-orange-500' : 'bg-gray-300'
                      }`} />
                    )}
                  </div>
                ))}
              </div>
              <div className="flex justify-between mt-3 text-sm font-medium">
                <span className={currentStep >= 1 ? 'text-orange-600' : 'text-gray-500'}>Product Definition</span>
                <span className={currentStep >= 2 ? 'text-orange-600' : 'text-gray-500'}>Market Context</span>
                <span className={currentStep >= 3 ? 'text-orange-600' : 'text-gray-500'}>Analysis Setup</span>
              </div>
            </div>

            {/* Example Button */}
            {currentStep === 1 && (
              <div className="mb-8 p-6 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Try an Example</h3>
                    <p className="text-gray-600">Load the fitness tracker example to see comprehensive market mapping</p>
                  </div>
                  <button
                    onClick={loadExample}
                    className="bg-orange-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-orange-600 transition-colors"
                  >
                    Load Fitness Tracker Example
                  </button>
                </div>
              </div>
            )}

            {/* Step 1: Product Definition */}
            {currentStep === 1 && (
              <div className="space-y-8">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold mb-3 text-gray-900">Define Your Product</h2>
                  <div className="w-24 h-1 bg-orange-500 mx-auto"></div>
                  <p className="text-lg text-gray-600 max-w-2xl mx-auto mt-4">
                    Tell us about your product to generate comprehensive market intelligence
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Product/Service Name *
                    </label>
                    <input
                      type="text"
                      value={formData.product_name}
                      onChange={(e) => handleInputChange('product_name', e.target.value)}
                      placeholder="e.g., Fitness Tracker"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Industry *
                    </label>
                    <input
                      type="text"
                      value={formData.industry}
                      onChange={(e) => handleInputChange('industry', e.target.value)}
                      placeholder="e.g., Wearable Technology"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Geography *
                    </label>
                    <input
                      type="text"
                      value={formData.geography}
                      onChange={(e) => handleInputChange('geography', e.target.value)}
                      placeholder="e.g., Global, United States, Europe"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Target Users *
                    </label>
                    <textarea
                      value={formData.target_user}
                      onChange={(e) => handleInputChange('target_user', e.target.value)}
                      placeholder="e.g., Health-conscious consumers, athletes, casual fitness enthusiasts"
                      rows={2}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>

                {/* File Upload Section */}
                <div className="mt-8 max-w-4xl mx-auto">
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 bg-gray-50 hover:border-orange-400 transition-colors">
                    <div className="text-center">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <h3 className="mt-2 text-sm font-semibold text-gray-900">Upload Supporting Documents (Optional)</h3>
                      <p className="mt-1 text-sm text-gray-600">
                        Upload industry reports, competitive analysis, or strategy documents (PDF, PPT)
                      </p>
                      <p className="mt-1 text-xs text-orange-600 font-semibold">
                        ⚠️ Hold Ctrl (Windows) or Cmd (Mac) to select multiple files
                      </p>
                      <div className="mt-4 flex flex-col items-center space-y-3">
                        <input
                          id="file-upload"
                          type="file"
                          multiple
                          accept=".pdf,.ppt,.pptx"
                          onChange={handleFileUpload}
                          disabled={isUploading}
                          style={{ display: 'none' }}
                        />
                        <label 
                          htmlFor="file-upload"
                          className="cursor-pointer bg-orange-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors inline-block shadow-md"
                        >
                          {isUploading ? '⏳ Uploading...' : '📁 Select Files (Multiple Allowed)'}
                        </label>
                        
                        <div className="bg-blue-50 border border-blue-200 rounded p-3 text-left max-w-md">
                          <p className="text-xs font-semibold text-blue-900 mb-2">📋 How to select multiple files:</p>
                          <ul className="text-xs text-blue-800 space-y-1">
                            <li>• <strong>Windows/Linux:</strong> Hold <kbd className="px-1 py-0.5 bg-white border rounded">Ctrl</kbd> while clicking files</li>
                            <li>• <strong>Mac:</strong> Hold <kbd className="px-1 py-0.5 bg-white border rounded">⌘ Cmd</kbd> while clicking files</li>
                            <li>• Click multiple files before pressing "Open"</li>
                          </ul>
                        </div>
                      </div>
                      <p className="mt-2 text-xs text-gray-500">Supports PDF and PowerPoint files, max 50MB each</p>
                    </div>

                    {/* Uploaded Files Display */}
                    {uploadedFiles.length > 0 && (
                      <div className="mt-6">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="text-sm font-semibold text-gray-900">
                            Uploaded Documents ({uploadedFiles.length})
                            {uploadedFiles.length === 1 && (
                              <span className="ml-2 text-xs text-orange-600 font-normal">
                                ⚠️ Only 1 file detected. Use Ctrl/Cmd+Click to select multiple files.
                              </span>
                            )}
                            {uploadedFiles.length > 1 && (
                              <span className="ml-2 text-xs text-green-600 font-normal">
                                ✓ Multiple files selected successfully!
                              </span>
                            )}
                          </h4>
                          <button
                            onClick={removeUploadedFiles}
                            className="text-xs text-red-600 hover:text-red-800"
                          >
                            Remove All
                          </button>
                        </div>
                        <div className="space-y-2">
                          {uploadedFiles.map((file, index) => (
                            <div key={index} className="flex items-center bg-white px-3 py-2 rounded border border-gray-200">
                              <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              <span className="text-sm text-gray-700 truncate flex-1">{file.name}</span>
                              <span className="text-xs text-gray-500 ml-2">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                            </div>
                          ))}
                        </div>
                        {uploadedDocData && (
                          <div className="mt-3 space-y-2">
                            <div className="p-3 bg-green-50 border border-green-200 rounded text-xs text-green-800">
                              <div className="font-semibold mb-1">✓ Documents Processed: {uploadedDocData.documents_processed}</div>
                              <div className="grid grid-cols-3 gap-2 mt-2">
                                <div>
                                  <span className="font-medium">Market Sizes:</span> {uploadedDocData.market_sizes?.length || 0}
                                </div>
                                <div>
                                  <span className="font-medium">Growth Rates:</span> {uploadedDocData.growth_rates?.length || 0}
                                </div>
                                <div>
                                  <span className="font-medium">Competitors:</span> {uploadedDocData.competitor_names?.length || 0}
                                </div>
                              </div>
                            </div>
                            {uploadedDocData.document_summaries && uploadedDocData.document_summaries.length > 0 && (
                              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                                <div className="text-xs font-semibold text-blue-900 mb-2">Document Details:</div>
                                <div className="space-y-1">
                                  {uploadedDocData.document_summaries.map((doc, idx) => (
                                    <div key={idx} className="text-xs text-blue-800">
                                      • {doc.file} - {doc.data_points} data points, {(doc.text_length / 1000).toFixed(1)}K characters
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Market Context */}
            {currentStep === 2 && (
              <div className="space-y-8">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold mb-3 text-gray-900">Market Context</h2>
                  <div className="w-24 h-1 bg-orange-500 mx-auto"></div>
                  <p className="text-lg text-gray-600 max-w-2xl mx-auto mt-4">
                    Help us understand your market dynamics and business model
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Primary Market Drivers *
                    </label>
                    <textarea
                      value={formData.demand_driver}
                      onChange={(e) => handleInputChange('demand_driver', e.target.value)}
                      placeholder="e.g., Health and wellness trends, preventive healthcare, aging population"
                      rows={2}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Revenue Model *
                    </label>
                    <select
                      value={formData.transaction_type}
                      onChange={(e) => handleInputChange('transaction_type', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    >
                      <option value="">Select revenue model...</option>
                      <option value="One-time Purchase">One-time Purchase</option>
                      <option value="Subscription">Subscription</option>
                      <option value="Freemium">Freemium</option>
                      <option value="Usage-based">Usage-based</option>
                      <option value="Commission-based">Commission-based</option>
                      <option value="Advertising">Advertising</option>
                    </select>
                  </div>

                  <div className="md:col-span-2 space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Key Performance Metrics *
                    </label>
                    <textarea
                      value={formData.key_metrics}
                      onChange={(e) => handleInputChange('key_metrics', e.target.value)}
                      placeholder="e.g., Device sales, subscription revenue, user engagement, retention rate"
                      rows={2}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Analysis Setup */}
            {currentStep === 3 && (
              <div className="space-y-8">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold mb-3 text-gray-900">Analysis Configuration</h2>
                  <div className="w-24 h-1 bg-orange-500 mx-auto"></div>
                  <p className="text-lg text-gray-600 max-w-2xl mx-auto mt-4">
                    Add any known market data to enhance the analysis accuracy
                  </p>
                </div>

                <div className="space-y-6 max-w-4xl mx-auto">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Known Market Benchmarks (Optional)
                    </label>
                    <textarea
                      value={formData.benchmarks}
                      onChange={(e) => handleInputChange('benchmarks', e.target.value)}
                      placeholder="e.g., Wearable tech market growing at 9.2% CAGR, $97.4B global market size"
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    />
                  </div>

                  {/* Review Summary */}
                  <div className="bg-gray-50 p-6 rounded-lg border">
                    <h3 className="text-xl font-semibold text-gray-900 mb-4">Market Analysis Summary</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Product:</span>
                        <span className="text-gray-900">{formData.product_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Industry:</span>
                        <span className="text-gray-900">{formData.industry}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Geography:</span>
                        <span className="text-gray-900">{formData.geography}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-600">Revenue Model:</span>
                        <span className="text-gray-900">{formData.transaction_type}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-12 pt-6 border-t border-gray-200">
              {/* Back Button - Hidden during analysis */}
              {!isAnalyzing && (
                <button
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Back
                </button>
              )}

              {/* Subtle back option during analysis */}
              {isAnalyzing && (
                <div className="flex items-center">
                  <button
                    onClick={() => {
                      if (window.confirm('Are you sure you want to cancel the analysis? This will stop the current process.')) {
                        setIsAnalyzing(false);
                        setAnalysisProgress(prev => ({
                          ...prev,
                          currentStep: 0,
                          stepName: '',
                          estimatedTimeLeft: 0
                        }));
                      }
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                  >
                    Cancel Analysis
                  </button>
                </div>
              )}

              {currentStep < 3 ? (
                <button
                  onClick={nextStep}
                  disabled={currentStep === 1 && !formData.product_name}
                  className="bg-orange-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              ) : (
                <div className="flex flex-col items-end">
                  <button
                    onClick={analyzeMarket}
                    disabled={isAnalyzing || !formData.product_name}
                    className="bg-orange-500 text-white px-6 py-2 rounded-lg font-semibold hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isAnalyzing ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="font-medium">Generating Market Map...</span>
                      </>
                    ) : (
                      'Generate Market Map'
                    )}
                  </button>

                  {/* Progress Indicator - Full Width Prominent Modal */}
                  {isAnalyzing && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                      <div className="bg-white p-6 sm:p-8 rounded-xl shadow-2xl border-2 border-orange-200 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                        <AgentSwarmProgress active={isAnalyzing} estimatedDurationSec={150} />

                        {/* Cancel Button */}
                        <div className="mt-8 pt-6 border-t-2 border-gray-200 flex justify-center">
                          <button
                            data-testid="cancel-analysis-btn"
                            onClick={() => {
                              if (window.confirm('Are you sure you want to cancel the analysis? This will stop the current process.')) {
                                setIsAnalyzing(false);
                                setAnalysisProgress(prev => ({
                                  ...prev,
                                  currentStep: 0,
                                  stepName: '',
                                  estimatedTimeLeft: 0
                                }));
                              }
                            }}
                            className="px-8 py-3 bg-red-500 text-white rounded-lg text-base font-semibold hover:bg-red-600 transition-colors shadow-lg hover:shadow-xl flex items-center space-x-2"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <span>Cancel Analysis</span>
                          </button>
                        </div>

                        {/* Help text */}
                        <div className="mt-5">
                          <p className="text-xs text-gray-500 text-center">
                            BCM Agent Swarm · GPT-4o primary with Claude Sonnet 4 fallback · Powered by Emergent LLM
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
  );
}
